import { useState, useEffect, useRef } from "react";
import { getApiUrl } from "../../../../api";
import { useAuthStore } from "../../../../store/authStore";
import { ChevronDown, ChevronUp, SettingsIcon } from "../accessibility/AccessibilityIcons";
// =============================================
// API
// =============================================
async function patchLayout(solution_code: string, body: any) {
  const res = await fetch(getApiUrl(`/api/solutions/${solution_code}/layout`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("cms_access_token")}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("저장 실패");
  return res.json();
}

async function uploadImage(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(getApiUrl("/api/images/upload"), {
    method: "POST",
    headers: { Authorization: `Bearer ${localStorage.getItem("cms_access_token")}` },
    body: formData,
  });
  if (!res.ok) throw new Error("이미지 업로드 실패");
  return res.json(); // { url, filename }
}

interface ContentProps {
  settingData: any;
  solutionCode: string;
  saveRef: React.MutableRefObject<(() => Promise<void>) | null>;
  showModal: (title: string) => void;
}

// =============================================
// 기관명 설정
// =============================================
function OrganizationNameContent({ settingData, solutionCode, saveRef, showModal }: ContentProps) {
  const [mode, setMode] = useState<"name" | "logo">("name");
  const [storeName, setStoreName] = useState("");
  const [fileName, setFileName] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const kioskCode = localStorage.getItem("kiosk_code") || "";

  useEffect(() => {
    if (!kioskCode) return;
    fetch(getApiUrl(`/api/kiosks/${kioskCode}/store-name`), {
      headers: { Authorization: `Bearer ${localStorage.getItem("cms_access_token")}` },
    })
      .then(res => res.json())
      .then(data => setStoreName(data.store_name ?? ""))
      .catch(() => {});
  }, [kioskCode]);

  useEffect(() => {
    if (!settingData) return;
    const url = settingData.layout.logo_url ?? "";
    setFileName(url ? url.split("/").pop() : "");
    setFileUrl(url);
    setMode(settingData.layout.logo_enabled === 1 ? "logo" : "name");
  }, [settingData]);

  saveRef.current = async () => {
    if (mode === "logo" && !fileName) {
      alert("기관로고 이미지를 선택해주세요.");
      return;
    }
    await patchLayout(solutionCode, mode === "logo" ? { logo_enabled: 1, logo_url: fileUrl } : { logo_enabled: 0 });
    showModal("기관명 설정이 저장되었습니다.");
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const result = await uploadImage(file);
      setFileName(result.filename);
      setFileUrl(result.url);
    } catch {
      alert("이미지 업로드 실패");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <p className="text-[12px] text-gray-400 mb-4">메인 화면 최상단 기관명 또는 기관로고 설정</p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        {/* 기관명 */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer w-[80px] sm:w-auto shrink-0">
            <input type="radio" name="org-mode" value="name" checked={mode === "name"} onChange={() => setMode("name")} className="accent-[#887267] w-4 h-4" />
            <span className="text-[13px] font-semibold text-[#2a1f1a]">기관명</span>
          </label>
          <input value={storeName} readOnly placeholder="매장명" onClick={() => alert("매장명 변경은 매장관리에서 수정해주세요.")}
            className={`flex-1 min-w-0 sm:w-[260px] sm:flex-none h-[34px] border rounded-lg px-3 text-[13px] cursor-default outline-none ${mode === "name" ? "border-[#e8e0dc] bg-[#f5f2f0]" : "border-[#e8e0dc] bg-[#f5f2f0] text-gray-300 cursor-not-allowed"}`}
          />
        </div>
        {/* 기관로고 */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer w-[80px] sm:w-auto shrink-0">
            <input type="radio" name="org-mode" value="logo" checked={mode === "logo"} onChange={() => setMode("logo")} className="accent-[#887267] w-4 h-4" />
            <span className="text-[13px] font-semibold text-[#2a1f1a]">기관로고</span>
          </label>
          <div className="flex items-center gap-2 flex-1 min-w-0 sm:flex-none">
            <label className={`h-[34px] px-3 rounded-lg text-[12px] font-medium border shrink-0 flex items-center ${mode === "logo" ? "bg-white text-[#887267] border-[#887267] cursor-pointer hover:bg-[#f5f2f0]" : "bg-[#f5f2f0] text-gray-300 border-[#e8e0dc] pointer-events-none"}`}>
              {uploading ? "업로드 중..." : (fileName ? "이미지 변경" : "첨부할 이미지 선택")}
              <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} disabled={mode !== "logo"} />
            </label>
            <input value={fileName} readOnly placeholder="파일명.png"
              className={`flex-1 min-w-0 sm:w-[300px] sm:flex-none h-[34px] border rounded-lg px-3 text-[12px] truncate ${mode === "logo" ? "border-[#e8e0dc] bg-white" : "border-[#e8e0dc] bg-[#f5f2f0] text-gray-300"}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================
// 배너 이미지 설정
// =============================================
function BannerImageContent({ settingData, solutionCode, saveRef, showModal }: ContentProps) {
  const [fileName, setFileName] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!settingData) return;
    const url = settingData.layout.main_image_url ?? "";
    setFileUrl(url);
    setFileName(url ? url.split("/").pop() : "");
  }, [settingData]);

  saveRef.current = async () => {
    await patchLayout(solutionCode, { main_image_url: fileUrl });
    showModal("배너 이미지 설정이 저장되었습니다.");
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const result = await uploadImage(file);
      setFileName(result.filename);
      setFileUrl(result.url);
    } catch {
      alert("이미지 업로드 실패");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <p className="text-[12px] text-gray-400 mb-4">메인 화면 배너이미지 설정</p>
      <div className="flex items-center gap-2 sm:gap-3">
        <span className="text-[13px] font-semibold text-[#2a1f1a] shrink-0">배너이미지</span>
        <label className="h-[34px] px-3 bg-white text-[#887267] border-[#887267] border rounded-lg text-[12px] font-medium hover:bg-[#f5f2f0] transition-colors shrink-0 flex items-center cursor-pointer">
          {uploading ? "업로드 중..." : (fileName ? "이미지 변경" : "첨부할 이미지 선택")}
          <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
        </label>
        <input value={fileName} readOnly placeholder="파일명.png"
          className="flex-1 sm:w-[300px] sm:flex-none h-[34px] border border-[#e8e0dc] rounded-lg px-3 text-[12px] bg-white truncate" />
      </div>
      <p className="text-[11px] text-gray-400 mt-3">※ 배너 이미지 권장 해상도: 1080 × 300px</p>
    </div>
  );
}

// =============================================
// 디자인 색상 설정
// =============================================
function ColorSettingContent({ settingData, solutionCode, saveRef, showModal }: ContentProps) {
  const colorModes = [
    { key: "blue", label: "블루모드", main: "#002879", sub: "#0040b8" },
    { key: "purple", label: "퍼플모드", main: "#54007B", sub: "#7800AF" },
    { key: "green", label: "그린모드", main: "#005B50", sub: "#008369" },
  ];
  const [selected, setSelected] = useState("blue");

  useEffect(() => {
    if (!settingData) return;
    const mainColor = settingData.layout.main_color;
    const matched = colorModes.find(m => m.main.toLowerCase() === mainColor?.toLowerCase());
    if (matched) setSelected(matched.key);
  }, [settingData]);

  saveRef.current = async () => {
    const mode = colorModes.find(m => m.key === selected)!;
    await patchLayout(solutionCode, { main_color: mode.main, sub_color: mode.sub });
    showModal("디자인 색상 설정이 저장되었습니다.");
  };

  return (
    <div>
      <p className="text-[12px] text-gray-400 mb-4">키오스크 화면 디자인 색상 선택</p>
      <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-12">
        {colorModes.map(mode => (
          <div key={mode.key} className="flex items-center sm:flex-col sm:items-start gap-2">
            <label className="flex items-center gap-2 cursor-pointer shrink-0">
              <input type="radio" name="color-mode" value={mode.key} checked={selected === mode.key} onChange={() => setSelected(mode.key)} className="accent-[#887267] w-4 h-4" />
              <span className="text-[13px] font-semibold text-[#2a1f1a]">{mode.label}</span>
            </label>
            <div className="flex gap-2 sm:pl-6">
              {[mode.main, mode.sub].map(color => (
                <div key={color} style={{ backgroundColor: color }} className="w-[70px] sm:w-[100px] h-[28px] sm:h-[32px] rounded-lg text-white text-[12px] font-medium flex items-center justify-center">
                  {color.toUpperCase()}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================
// 화면타입 설정
// =============================================
function ScreenTypeContent({ settingData, solutionCode, saveRef, showModal }: ContentProps) {
  const [avatarEnabled, setAvatarEnabled] = useState(false);
  const [type, setType] = useState("a");

  useEffect(() => {
    if (!settingData) return;
    setAvatarEnabled(settingData.layout.avatar_enabled === 1);
    setType(settingData.layout.layout_type ?? "a");
  }, [settingData]);

  saveRef.current = async () => {
    await patchLayout(solutionCode, { avatar_enabled: avatarEnabled ? 1 : 0, layout_type: type });
    showModal("화면타입 설정이 저장되었습니다.");
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <span className="text-[13px] font-semibold text-[#2a1f1a]">아바타</span>
        <button onClick={() => setAvatarEnabled(prev => !prev)}
          className={`relative w-[90px] h-[28px] rounded-full transition-colors duration-200 ${avatarEnabled ? "bg-[#887267]" : "bg-[#d0c4be]"}`}>
          <span className={`absolute inset-0 flex items-center text-white text-[12px] ${avatarEnabled ? "justify-start pl-3" : "justify-end pr-3"}`}>
            {avatarEnabled ? "사용" : "미사용"}
          </span>
          <span className={`absolute top-[3px] w-[40px] h-[22px] bg-white rounded-full shadow transition-all duration-200 ${avatarEnabled ? "left-[45px]" : "left-[3px]"}`} />
        </button>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" name="screen-type" value="a" checked={type === "a"} onChange={() => setType("a")} className="accent-[#887267] w-4 h-4" />
          <span className="text-[13px] text-[#2a1f1a]">도움기능 버튼 6개 ver.</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" name="screen-type" value="b" checked={type === "b"} onChange={() => setType("b")} className="accent-[#887267] w-4 h-4" />
          <span className="text-[13px] text-[#2a1f1a]">도움기능 버튼 5개 ver.</span>
        </label>
      </div>
    </div>
  );
}

// =============================================
// 아코디언
// =============================================
function AccordionItem({ itemKey, label, icon, isOpen, onToggle, ContentComponent, settingData, solutionCode, showModal }: {
  itemKey: string; label: string; icon: React.ReactNode; isOpen: boolean;
  onToggle: (key: string) => void; ContentComponent: React.ComponentType<ContentProps>;
  settingData: any; solutionCode: string; showModal: (title: string) => void;
}) {
  const saveRef = useRef<(() => Promise<void>) | null>(null);

  return (
    <div className="border border-[#e8e0dc] rounded-2xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
      <div className="w-full flex items-center justify-between px-5 h-[52px] cursor-pointer" onClick={() => onToggle(itemKey)}>
        <div className="flex items-center gap-2">
          <div className={isOpen ? "text-[#887267]" : "text-gray-400"}>{icon}</div>
          <span className={`text-[14px] font-semibold ${isOpen ? "text-[#2a1f1a]" : "text-gray-400"}`}>{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {isOpen && (
            <button onClick={e => { e.stopPropagation(); saveRef.current?.(); }}
              className="px-3 h-[28px] bg-white border border-[#887267] rounded-lg text-[#887267] text-[12px] font-medium hover:bg-[#f5f2f0] transition-colors">
              저장
            </button>
          )}
          {isOpen ? <ChevronUp /> : <ChevronDown />}
        </div>
      </div>
      {isOpen && (
        <div className="border-t border-[#f5f2f0] bg-white px-5 py-6">
          <ContentComponent settingData={settingData} solutionCode={solutionCode} saveRef={saveRef} showModal={showModal} />
        </div>
      )}
    </div>
  );
}

// =============================================
// 아이콘
// =============================================
function EditIcon() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>;
}
function ImageIcon() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>;
}
function PaletteIcon() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="13.5" cy="6.5" r=".5" /><circle cx="17.5" cy="10.5" r=".5" /><circle cx="8.5" cy="7.5" r=".5" /><circle cx="6.5" cy="12.5" r=".5" /><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 011.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" /></svg>;
}
function LayoutIcon() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>;
}

// =============================================
// 메인
// =============================================
export default function AdminDisplaySettings() {
  const { role } = useAuthStore();
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [modal, setModal] = useState({ isOpen: false, title: "" });
  const [settingData, setSettingData] = useState<any>(null);
  const [solutionCode, setSolutionCode] = useState("");
  const [loading, setLoading] = useState(true);

  const kioskCode = localStorage.getItem("kiosk_code") || "";

  useEffect(() => {
    if (!kioskCode) { setLoading(false); return; }
    fetch(getApiUrl(`/api/solutions/by-kiosk/${kioskCode}/settings`), {
      headers: { Authorization: `Bearer ${localStorage.getItem("cms_access_token")}` },
    })
      .then(res => res.json())
      .then(data => { setSettingData(data); setSolutionCode(data.solution_code); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [kioskCode]);

  const showModal = (title: string) => setModal({ isOpen: true, title });
  const toggleItem = (key: string) => setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));

  const settingItems = [
    { key: "기관명", label: "기관명 설정", component: OrganizationNameContent, icon: <EditIcon /> },
    { key: "배너", label: "배너이미지 설정", component: BannerImageContent, icon: <ImageIcon /> },
    { key: "색상", label: "디자인 색상 설정", component: ColorSettingContent, icon: <PaletteIcon /> },
    ...(role > 1 ? [{ key: "화면타입", label: "화면타입 설정", component: ScreenTypeContent, icon: <LayoutIcon /> }] : []),
  ];
  return (
    <div className="bg-[#f5f2f0] min-h-screen p-6">
      <div className="max-w-[1000px] mx-auto">

        <div className="flex items-center gap-3 mb-6">
          <SettingsIcon />
          <h1 className="text-[22px] font-extrabold text-[#2a1f1a]">화면 디자인 설정</h1>
        </div>

        {loading ? (
          <div className="py-20 text-center text-gray-400 text-[14px]">불러오는 중...</div>
        ) : (
          <div className="flex flex-col gap-3">
            {settingItems.map(item => (
              <AccordionItem
                key={item.key}
                itemKey={item.key}
                label={item.label}
                icon={item.icon}
                isOpen={!!openItems[item.key]}
                onToggle={toggleItem}
                ContentComponent={item.component}
                settingData={settingData}
                solutionCode={solutionCode}
                showModal={showModal}
              />
            ))}
          </div>
        )}
      </div>

      {/* 결과 모달 */}
      {modal.isOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[320px] mx-4 p-6 text-center">
            <p className="text-[14px] font-semibold text-[#2a1f1a] mb-4">{modal.title}</p>
            <button onClick={() => setModal({ isOpen: false, title: "" })} className="w-full py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold">확인</button>
          </div>
        </div>
      )}
    </div>
  );
}